import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
  HelpCircle, MessageCircle, FileText, ChevronDown, ChevronRight, 
  Send, Search, Ticket, Clock, CheckCircle, AlertCircle, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQ_DATA = [
  {
    category: "Compte & Profil",
    questions: [
      {
        q: "Comment changer ma photo de profil?",
        a: "Va dans les Paramètres > Profil, puis clique sur l'icône de caméra sur ton avatar pour uploader une nouvelle photo."
      },
      {
        q: "Comment devenir créateur vérifié?",
        a: "Les créateurs vérifiés sont sélectionnés par notre équipe. Ajoute tes réseaux sociaux (TikTok, Instagram) à ton profil pour augmenter tes chances."
      },
      {
        q: "Puis-je changer mon nom d'utilisateur?",
        a: "Oui, tu peux modifier ton nom d'utilisateur dans Paramètres > Profil. Attention, l'URL de ton profil changera aussi."
      }
    ]
  },
  {
    category: "BARKA & Récompenses",
    questions: [
      {
        q: "Comment gagner des points BARKA?",
        a: "Tu gagnes des BARKA en: cuisinant des recettes (+50), créant des stories (+2), votant pour des validations (+1), gagnant des défis (+100-500), et recevant des likes."
      },
      {
        q: "Pourquoi mes BARKA sont-ils en attente?",
        a: "Quand tu cuisines une recette, tu reçois +5 BARKA immédiatement. Les +45 restants sont débloqués après validation par la communauté (3 votes positifs minimum)."
      },
      {
        q: "Puis-je convertir mes BARKA en argent?",
        a: "Les BARKA représentent ta réputation et ne sont pas convertibles en argent. Cependant, les créateurs peuvent gagner des TND via les pourboires et les prix des défis."
      }
    ]
  },
  {
    category: "Défis & Recettes",
    questions: [
      {
        q: "Comment participer à un défi?",
        a: "Va dans l'onglet Challenges, choisis un défi actif, et soumets ta création (photo/vidéo) avant la date limite. La communauté votera ensuite pour les meilleures entrées."
      },
      {
        q: "Pourquoi ma preuve de cuisine a été rejetée?",
        a: "Une preuve peut être rejetée si: la photo ne montre pas clairement le plat, il ne correspond pas à la recette, ou elle semble être tirée d'internet."
      },
      {
        q: "Combien de fois puis-je cuisiner la même recette?",
        a: "Tu peux cuisiner chaque recette une fois toutes les 24 heures. Cela évite les abus et encourage à essayer différentes recettes."
      }
    ]
  },
  {
    category: "Paiements & Créateurs",
    questions: [
      {
        q: "Comment recevoir des pourboires?",
        a: "Les visiteurs de ton profil peuvent te donner des pourboires. L'argent va dans ton portefeuille créateur (visible dans Dashboard Créateur)."
      },
      {
        q: "Quelles sont les commissions de la plateforme?",
        a: "Plan Gratuit: 20%, Premium: 15%, Pro: 10%. Tu gardes le reste de chaque pourboire et prix de défi."
      },
      {
        q: "Comment retirer mes gains?",
        a: "Va dans Dashboard Créateur > Retirer. Le minimum est de 50 TND. Les retraits sont traités sous 3-5 jours ouvrables."
      }
    ]
  }
];

export default function SupportPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('faq');
  const [searchQuery, setSearchQuery] = useState('');
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTicket, setNewTicket] = useState({
    category: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchMyTickets();
    }
  }, [activeTab]);

  const fetchMyTickets = async () => {
    try {
      const response = await api.get('/support/tickets');
      setMyTickets(response.data.tickets || []);
    } catch (error) {
      console.log('No tickets');
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.category || !newTicket.subject || !newTicket.message) {
      toast.error('Remplis tous les champs');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/support/tickets', newTicket);
      toast.success('Ticket envoyé! On te répond sous 24-48h.');
      setNewTicket({ category: '', subject: '', message: '' });
      setActiveTab('tickets');
      fetchMyTickets();
    } catch (error) {
      toast.error('Erreur d\'envoi');
    } finally {
      setLoading(false);
    }
  };

  // Filter FAQ based on search
  const filteredFaq = searchQuery
    ? FAQ_DATA.map(cat => ({
        ...cat,
        questions: cat.questions.filter(q => 
          q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.questions.length > 0)
    : FAQ_DATA;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="support-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1E4ED8] to-[#0D3A9E] rounded-2xl p-6 mb-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <HelpCircle className="w-7 h-7" />
          </div>
          <div>
            <h1 className="font-fraunces text-2xl font-bold">Centre d'aide</h1>
            <p className="text-white/80 text-sm">Comment pouvons-nous t'aider?</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-xl p-1 border border-gray-100">
        <button
          onClick={() => setActiveTab('faq')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-colors ${
            activeTab === 'faq' ? 'bg-[#1E4ED8] text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <FileText className="w-4 h-4" />
          FAQ
        </button>
        <button
          onClick={() => setActiveTab('contact')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-colors ${
            activeTab === 'contact' ? 'bg-[#1E4ED8] text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          Contacter
        </button>
        <button
          onClick={() => setActiveTab('tickets')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-colors ${
            activeTab === 'tickets' ? 'bg-[#1E4ED8] text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Ticket className="w-4 h-4" />
          Mes tickets
        </button>
      </div>

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <div className="space-y-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une question..."
              className="pl-12 h-12 rounded-xl"
            />
          </div>

          {/* FAQ Categories */}
          {filteredFaq.map((category, catIndex) => (
            <div key={catIndex} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <h3 className="font-semibold text-gray-900 px-6 py-4 bg-gray-50 border-b border-gray-100">
                {category.category}
              </h3>
              <Accordion type="single" collapsible className="px-2">
                {category.questions.map((item, qIndex) => (
                  <AccordionItem key={qIndex} value={`${catIndex}-${qIndex}`} className="border-none">
                    <AccordionTrigger className="px-4 py-4 text-left text-sm font-medium hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-sm text-gray-600">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          {filteredFaq.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucune question trouvée pour "{searchQuery}"
            </div>
          )}
        </div>
      )}

      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="font-semibold text-lg text-gray-900 mb-4">Envoyer un ticket</h2>
          
          <form onSubmit={handleSubmitTicket} className="space-y-4">
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={newTicket.category} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionne une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="account">Compte & Profil</SelectItem>
                  <SelectItem value="payment">Paiements & Retraits</SelectItem>
                  <SelectItem value="bug">Bug / Problème technique</SelectItem>
                  <SelectItem value="content">Signalement de contenu</SelectItem>
                  <SelectItem value="challenge">Défis & Compétitions</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Résume ton problème en quelques mots"
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                placeholder="Décris ton problème en détail. Plus tu donnes d'informations, plus on peut t'aider rapidement."
                className="w-full p-3 border border-gray-200 rounded-xl resize-none h-32 focus:ring-2 focus:ring-[#1E4ED8]/20 focus:border-[#1E4ED8] transition-colors"
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-[#1E4ED8]">
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Envoi...' : 'Envoyer le ticket'}
            </Button>
          </form>

          {/* Contact Info */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-medium text-gray-900 mb-3">Autres moyens de contact</h3>
            <div className="space-y-2">
              <a href="mailto:support@mharher.tn" className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <Mail className="w-5 h-5 text-[#1E4ED8]" />
                <span className="text-sm text-gray-700">support@mharher.tn</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="space-y-4">
          {myTickets.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
              <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">Aucun ticket</h3>
              <p className="text-sm text-gray-500 mb-4">Tu n'as pas encore envoyé de ticket support.</p>
              <Button onClick={() => setActiveTab('contact')} variant="outline">
                Créer un ticket
              </Button>
            </div>
          ) : (
            myTickets.map((ticket) => (
              <div key={ticket.id} className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">{ticket.subject}</h4>
                    <p className="text-xs text-gray-500">{ticket.category} • {new Date(ticket.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                    ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {ticket.status === 'open' ? 'Ouvert' : ticket.status === 'in_progress' ? 'En cours' : 'Résolu'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{ticket.message}</p>
                
                {ticket.admin_response && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-[#1E4ED8]">
                    <p className="text-xs font-medium text-[#1E4ED8] mb-1">Réponse du support</p>
                    <p className="text-sm text-gray-700">{ticket.admin_response}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
